import { sortBy } from 'lodash-es';
import {
  arrayOf,
  func,
  number,
  oneOf
} from 'prop-types';

import React, { useMemo, useState } from 'react';

import { participantErrors, participantWarnings } from '../../../constants';
import { participantIdTypeValues, participantType, surveyTypeValues } from '../../../constants/types';
import { withLang } from '../../../i18n';
import foldableListStyles from '../../FoldableList/foldableList.module.css'; // NOTE: Leaving this here for the styling of the table

import Pagination from '../../Pagination';
import StudentRow from './StudentRow';
import TeacherRow from './TeacherRow';

const PAGINATION_THRESHOLD = 10;
const NON_ERROR_COMPARE_INDEX = 999;
const { STUDENT_NUMBER, EMAIL } = participantIdTypeValues;

const getParticipantSortKey = (idType = STUDENT_NUMBER) => (participant) => {
  let priority;
  const {
    error, warning, firstName = '', lastName = ''
  } = participant;
  const id = participant[idType];
  if (error) {
    priority = Object.values(participantErrors).indexOf(error);
  } else if (warning) {
    priority = Object.values(participantWarnings).indexOf(warning);
  } else {
    priority = NON_ERROR_COMPARE_INDEX;
  }
  return [priority, lastName.toLowerCase(), firstName.toLowerCase(), id];
};

const ParticipantList = ({
  count,
  surveyType,
  participants,
  editParticipant,
  selectParticipantEmail,
  translate: t
}) => {
  const isHUTSurvey = surveyType === surveyTypeValues.HUT;
  const participantIdType = isHUTSurvey ? EMAIL : STUDENT_NUMBER;
  const RowComponent = isHUTSurvey ? TeacherRow : StudentRow;

  // INFO: Pagination setttings and variables
  const [page, setPage] = useState(0);
  const totalPages = useMemo(() => Math.round(participants.length / PAGINATION_THRESHOLD), [participants]);
  const isFirstPage = useMemo(() => page <= 0, [page]);
  const isLastPage = useMemo(() => page >= totalPages - 1, [page, totalPages]);

  const sortedParticipants = useMemo(() =>
    sortBy(participants, getParticipantSortKey(participantIdType)),
  [participants, participantIdType]);

  const paginatedParticipants = useMemo(() => {
    const sliceStart = page === 0 ? 0 : (PAGINATION_THRESHOLD * page);
    const sliceEnd = page === 0 ? PAGINATION_THRESHOLD : (PAGINATION_THRESHOLD * page) + PAGINATION_THRESHOLD;
    return participants.slice(sliceStart, sliceEnd);
  }, [page, sortedParticipants]);

  const reservedEmails = participants.map((p) => p.email?.toLowerCase());

  const generateRows = (participantsList) =>
    participantsList.map((participant) => {
      const name = participant.name || participant.commonName || [participant.firstName, participant.lastName].join(' ');
      let editFn = () => null;
      if (participant.canEditEmail) {
        editFn = participant.existingEmail ? selectParticipantEmail : editParticipant;
      }
      const rowProps = isHUTSurvey
        ? { participant, name }
        : {
          participant, name, reservedEmails, editParticipant: editFn
        };

      return <RowComponent key={participant[participantIdType]} {...rowProps} />;
    });

  const tableHeaders = isHUTSurvey
    ? [t('common.name'), t('common.email'), '']
    : [t('common.name'), t('common.email'), t('common.studentNumber'), ''];

  const renderParticipants = (participantsList) => (
    <div className={foldableListStyles.listEntries}>
      <table data-cy="survey-participants-list-table">
        <thead>
          <tr className={foldableListStyles.headers}>
            {tableHeaders.map((header) => <th key={header}>{header}</th>)}
          </tr>
        </thead>
        <tbody data-cy="survey-participants-list-body">
          {generateRows(participantsList)}
        </tbody>
      </table>
    </div>
  );
  const title = isHUTSurvey
    ? <h3>{t('surveyParticipantsEditor.teacherParticipantsEditor.recipients')}</h3>
    : <h3>{t('startSurvey.recipients')}{count ? ` (${count})` : ''}</h3>;

  return (
    <div>
      {title}
      {(participants && participants.length > 0) && renderParticipants(paginatedParticipants)}
      {(participants && participants.length > PAGINATION_THRESHOLD)
        && (
          <Pagination
            navFunction={setPage}
            totalPages={totalPages}
            first={isFirstPage}
            last={isLastPage}
            number={page}
            translate={t}
          />
        )}
    </div>
  );
};

ParticipantList.propTypes = {
  surveyType: oneOf(Object.values(surveyTypeValues)),
  participants: arrayOf(participantType).isRequired,
  editParticipant: func,
  selectParticipantEmail: func,
  translate: func.isRequired,
  count: number.isRequired
};

ParticipantList.defaultProps = {
  surveyType: null,
  editParticipant: null,
  selectParticipantEmail: null
};

export default withLang(ParticipantList);
